import { isStartSchedule } from './taskSchedule';

export const capitalize = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export const generateId = (): string => {
    // Prefer cryptographically strong UUIDs when available to avoid collisions in fast successive calls
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // Fallback: timestamp + random suffix
    const randomPart = Math.random().toString(36).slice(2, 10);
    return `${Date.now()}-${randomPart}`;
}

export const autoResizeTextarea = (event: { target: HTMLTextAreaElement }): void => {
    const textArea = event.target;
    const originalOverflow = textArea.style.overflow;
    const minHeight = parseFloat(getComputedStyle(textArea).minHeight);
    const maxHeight = parseFloat(getComputedStyle(textArea).maxHeight);
    const lineHeight = 24;

    // Reset height to auto to get the natural scrollHeight
    textArea.style.height = 'auto';
    textArea.style.overflow = 'hidden';
    
    // Calculate new height based on scrollHeight
    const newHeight = Math.min(textArea.scrollHeight, maxHeight) + 4;
    
    // Use lineHeight for single-line text to maintain original height
    const finalHeight = textArea.value.includes('\n') || textArea.scrollHeight > lineHeight + 8
        ? Math.max(newHeight, minHeight)
        : Math.max(lineHeight, minHeight);

    textArea.style.height = `${finalHeight}px`;
    textArea.style.overflow = originalOverflow;
}

export const keepFocusedControlVisibleOnTouch = (element: HTMLElement): void => {
    if (
        typeof window === 'undefined'
        || typeof window.matchMedia !== 'function'
        || !window.matchMedia('(hover: none) and (pointer: coarse)').matches
    ) {
        return;
    }

    window.requestAnimationFrame(() => {
        element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
};

export const keepElementVisible = (element: HTMLElement): void => {
    window.requestAnimationFrame(() => {
        element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
};

export const handleShiftEnter = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    currentValue: string,
    setValue: (value: string) => void
): boolean => {
    if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        const textarea = e.currentTarget;
        const cursorPosition = textarea.selectionStart;
        const newValue = currentValue.slice(0, cursorPosition) + '\n' + currentValue.slice(cursorPosition);
        setValue(newValue);
        // Set cursor after the inserted newline
        setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd = cursorPosition + 1;
        }, 0);
        return true;
    }
    return false;
}

export const formatDate = (date: Date | string): string => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

interface RelativeNoteDateLabels {
    now: string;
    yesterday: string;
    minuteShort: string;
}

const defaultRelativeNoteDateLabels: RelativeNoteDateLabels = {
    now: 'Now',
    yesterday: 'Yesterday',
    minuteShort: 'min',
};

const isSameCalendarDay = (firstDate: Date, secondDate: Date) =>
    firstDate.getFullYear() === secondDate.getFullYear() &&
    firstDate.getMonth() === secondDate.getMonth() &&
    firstDate.getDate() === secondDate.getDate();

const formatTimeOnly = (date: Date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

const formatDateOnly = (date: Date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

export const formatNoteLastUpdated = (
    date: Date | string,
    labels: RelativeNoteDateLabels = defaultRelativeNoteDateLabels,
    now = new Date()
): string => {
    const updatedDate = new Date(date);
    if (Number.isNaN(updatedDate.getTime())) return '';

    const diffMs = now.getTime() - updatedDate.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    if (isSameCalendarDay(updatedDate, now)) {
        if (diffMinutes < 1) return labels.now;
        if (diffMinutes < 60) return `${diffMinutes} ${labels.minuteShort}`;
        return formatTimeOnly(updatedDate);
    }

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    if (isSameCalendarDay(updatedDate, yesterday)) {
        return labels.yesterday;
    }

    return formatDateOnly(updatedDate);
};

export const formatDateDisplay = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
};

export const formatTaskDateRange = (
    dueDate: string,
    schedule?: { endDate?: string; precision?: 'hour' | 'day' | 'month' | 'year' },
): string => {
    const dueDateValue = new Date(dueDate);
    const hasTime = dueDateValue.getHours() !== 0 || dueDateValue.getMinutes() !== 0;
    const showTime = schedule?.precision === 'hour' || (!schedule && hasTime);
    const start = showTime ? formatDateTimeDisplay(dueDate) : formatDateDisplay(dueDate);
    const end = schedule?.endDate
        ? showTime ? formatDateTimeDisplay(schedule.endDate) : formatDateDisplay(schedule.endDate)
        : '';
    return end && end !== start ? `${start} – ${end}` : start;
};

export const formatDateTimeDisplay = (dateTimeString: string): string => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day} ${month}, ${year}, ${hours}:${minutes}`;
};

export const isOverdue = (dueDate: string, duration?: number, schedule?: {
    precision: 'hour' | 'day' | 'month' | 'year';
    startDate?: string;
    endDate?: string;
    kind?: 'start' | 'deadline' | 'range';
}, now = new Date()): boolean => {
    const scheduledDate = schedule?.startDate || dueDate;
    if (!scheduledDate) return false;
    const due = new Date(scheduledDate);
    if (isStartSchedule(schedule)) return false;
    if (duration !== undefined) due.setMinutes(due.getMinutes() + duration);
    else if (schedule) {
        if (schedule.endDate) due.setTime(new Date(schedule.endDate).getTime());
        const hasExplicitTime = due.getHours() !== 0 || due.getMinutes() !== 0;
        if (hasExplicitTime) return due < now;
        if (schedule.precision === 'month') due.setMonth(due.getMonth() + 1, 0);
        if (schedule.precision === 'year') due.setFullYear(due.getFullYear(), 11, 31);
        due.setHours(23, 59, 59, 999);
    } else due.setHours(23, 59, 59, 999);
    return due < now;
};
